import React, { FC, useEffect, useMemo, useRef, useState, useCallback } from 'react';

import { Typography } from '@material-ui/core';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import { isWidthUp } from '@material-ui/core/withWidth';
import cx from 'classnames';
import parse from 'html-react-parser';
import Swiper from 'react-id-swiper';

import { BREAKPOINTS_VALUES, MIDDLE_MARGIN, SMALL_MARGIN, useWidth } from '../../theme';
import { WeAreBlock as WeAreBlockType } from '../../types';
import { parseHighlightedText } from '../../utils/helpers';
import { useEventListener } from '../../utils/hooks';
import useIsSsr from '../../utils/hooks/useIsSsr';
import useTextStyles from '../../utils/textStyles';
import { InfoItem } from './InfoItem';
import { SliderCard } from './SliderCard';
import useStyles from './styles';

const swiperSettings = {
  slidesPerView: 1.15,
  spaceBetween: SMALL_MARGIN,
  direction: 'horizontal',
  rebuildOnUpdate: true,
  pagination: {
    el: '.swiper-pagination',
    clickable: true,
  },
  breakpoints: {
    [BREAKPOINTS_VALUES.lg]: {
      slidesPerView: 1.5,
      spaceBetween: MIDDLE_MARGIN,
    },
  },
};

//calculate space after last slide to move it to container start
const calculateLastSlideOffset = (clientWidth: number, width: Breakpoint): number => {
  if (clientWidth) {
    const wrapperWidth = clientWidth;
    if (isWidthUp('lg', width)) {
      return (wrapperWidth / 1.5) * 0.5;
    } else {
      return (wrapperWidth / 1.15) * 0.15;
    }
  }

  return 0;
};

interface WeAreBlockProps {
  data?: WeAreBlockType;
}

const WeAreBlock: FC<WeAreBlockProps> = ({ data }) => {
  const classes = useStyles(data?.background)();
  const textClasses = useTextStyles();

  const width = useWidth();
  const isSsr = useIsSsr();
  const isMobile = !isWidthUp('md', width);

  const swiperWrapperRef = useRef<HTMLDivElement>(null);

  const [lastSlideOffset, setLastSlideOffset] = useState(0);

  const title = useMemo(
    () =>
      data?.heading
        ? parse(
            parseHighlightedText(
              data?.heading,
              (match) => `<span>${match.slice(2, -2)}</span><br />`,
            ),
          )
        : '',
    [data],
  );

  const settings = useMemo(() => {
    return {
      ...swiperSettings,
      slidesOffsetAfter: lastSlideOffset,
    };
  }, [lastSlideOffset]);

  const getLastSlideOffset = useCallback(() => {
    setLastSlideOffset(
      calculateLastSlideOffset(swiperWrapperRef?.current?.clientWidth || 0, width),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swiperWrapperRef.current, width]);

  useEventListener('resize', getLastSlideOffset);

  useEffect(() => {
    getLastSlideOffset();
  }, [getLastSlideOffset]);

  return (
    <div
      className={cx(classes.wrapper, data?.background === 'Dark' ? 'dark-block' : 'light-block')}
    >
      {isMobile && (
        <div className={classes.textWrapper}>
          <Typography variant="h2" className={cx(textClasses.h2, classes.header)}>
            {title}
          </Typography>
        </div>
      )}
      <div className={classes.container}>
        <div className={classes.col}>
          <div className={classes.textWrapper}>
            {!isMobile && (
              <Typography variant="h2" className={cx(textClasses.h2, classes.header)}>
                {title}
              </Typography>
            )}
            {Boolean(data?.items?.length) && !isSsr && (
              <div>
                {data?.items?.map((item) => (
                  <InfoItem
                    key={item?.icon}
                    iconUrl={item?.icon}
                    title={item?.title}
                    description={item?.description}
                    wrapperClass={classes.infoItem}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        {Boolean(data?.sliders?.length && data?.sliders?.length > 1) && (
          <div className={cx(classes.col, classes.rightColumn)} ref={swiperWrapperRef}>
            <Swiper {...settings}>
              {data?.sliders?.map((item) => (
                <div key={item?.image}>
                  <SliderCard imageUrl={item?.image} labelText={item?.label} />
                </div>
              ))}
            </Swiper>
          </div>
        )}
        {Boolean(data?.sliders?.length && data.sliders.length === 1 && data.sliders[0]) && (
          <div className={cx(classes.col, classes.rightColumn)}>
            <div className={classes.singleCardWrapper}>
              {/*@ts-ignore*/}
              <SliderCard imageUrl={data.sliders[0]?.image} labelText={data.sliders[0]?.label} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeAreBlock;
